import { useEffect, useState } from "react";

import { ApiError, apiRequest } from "../lib/api";

// ──── Wire shape from GET /api/profile/agent-preview ─────────────────────────
// Matches the Go ProfileResponse JSON keys (snake_case) for the agent lens.
// Only the subset the endpoint emits for the agent projection is modelled here.
// LO-only magnitudes (household_income, savings, monthly_debts, comfort_dti_pct,
// reserve_floor_months, estimated_dti, etc.) are NEVER in this type — they do
// not appear in the agent-preview response per the field-leak guard.

interface AffordabilityMovementWire {
  band_high_from: number | null;
  band_high_to: number | null;
}

// PassportPreviewWire is the nested object from GET /api/profile/agent-preview
// that carries the bucketed ranges for the self-preview card.
// Exact magnitudes (household_income, savings) are NEVER present here —
// only ranges (low/high pairs) and the anonymized handle.
interface PassportPreviewWire {
  overall_tier?: string | null;
  anonymized_handle?: string | null;
  // Estimated qualification range (from the affordability band).
  estimated_qualification_low?: number | null;
  estimated_qualification_high?: number | null;
  // Down-payment range (bucketed ±20% of savings).
  down_payment_low?: number | null;
  down_payment_high?: number | null;
  down_payment_tier?: string | null;
  // Household income range (bucketed ±15% of income — range only, never exact).
  household_income_low?: number | null;
  household_income_high?: number | null;
  household_income_tier?: string | null;
  // Credit band string (e.g. "700-739").
  credit_band?: string | null;
  credit_tier?: string | null;
  // Employment band (qualitative tenure, e.g. "2+ yrs").
  employment_band?: string | null;
  employment_tier?: string | null;
  // Monthly debt range (bucketed ±25% of total).
  monthly_debt_low?: number | null;
  monthly_debt_high?: number | null;
  monthly_debt_tier?: string | null;
}

interface AgentPreviewProfileWire {
  // Anonymized-safe
  buyer_type?: string | null;
  timeline?: string | null;
  target_area?: string | null;
  target_price_band?: string | null;
  price_kind?: "shopping_range" | "property_bracket" | "target_bracket";
  price_approximate?: boolean;
  profile_completeness?: number | null;
  // Status-only
  display_name?: string | null;
  housing_situation?: string | null;
  // Agent financial lens (qualitative, no raw magnitudes)
  credit_score_range?: string | null;
  employment_months?: number | null;
  target_price?: number | null;
  // Affordability overlay outcome (O6 — surfaced intentionally)
  affordability_band_low?: number | null;
  affordability_band_high?: number | null;
  sill_qual_price?: number | null;
  readiness_score?: number | null;
  readiness_segment?: string | null;
  readiness_label?: string | null;
  affordability_movement?: AffordabilityMovementWire | null;
  down_payment_ready?: boolean | null;
  // Passport preview — bucketed ranges for the self-preview card.
  // Populated by the backend's buildPassportPreview; replaces client-side
  // range derivation. Absent when the profile has no financial data yet.
  passport_preview?: PassportPreviewWire | null;
}

interface AgentPreviewEnvelope {
  profile: AgentPreviewProfileWire | null;
}

// ──── Caller-facing shape ─────────────────────────────────────────────────────
// Camel-cased; mirrors the wire shape without LO magnitudes.

export interface AffordabilityMovement {
  bandHighFrom: number | null;
  bandHighTo: number | null;
}

/**
 * PassportPreview holds the backend-derived bucketed ranges for the
 * "Passport preview · What agents see" card. Exact magnitudes are NEVER
 * present — only ranges (low/high pairs) and the anonymized handle.
 * Use these fields instead of client-side rule-of-thumb range derivation.
 */
export interface PassportPreview {
  overallTier?: string | null;
  anonymizedHandle: string | null;
  // Estimated qualification range (from the affordability band).
  estimatedQualificationLow: number | null;
  estimatedQualificationHigh: number | null;
  // Down-payment range (bucketed ±20% of savings).
  downPaymentLow: number | null;
  downPaymentHigh: number | null;
  downPaymentTier: string | null;
  // Household income range (bucketed ±15% — range only, never exact figure).
  householdIncomeLow: number | null;
  householdIncomeHigh: number | null;
  householdIncomeTier: string | null;
  // Credit band string (e.g. "700-739").
  creditBand: string | null;
  creditTier: string | null;
  // Employment band (qualitative tenure, e.g. "2+ yrs"). Null when absent.
  employmentBand: string | null;
  employmentTier: string | null;
  // Monthly debt range (bucketed ±25% of total). Null when absent.
  monthlyDebtLow: number | null;
  monthlyDebtHigh: number | null;
  monthlyDebtTier: string | null;
}

export interface AgentPreviewProfile {
  // Anonymized-safe
  buyerType: string | null;
  timeline: string | null;
  targetArea: string | null;
  targetPriceBand: string | null;
  priceKind?: "shopping_range" | "property_bracket" | "target_bracket";
  priceApproximate?: boolean;
  profileCompleteness: number | null;
  // Status-only
  displayName: string | null;
  housingSituation: string | null;
  // Agent financial lens
  creditScoreRange: string | null;
  employmentMonths: number | null;
  targetPrice: number | null;
  // Affordability overlay outcome
  affordabilityBandLow: number | null;
  affordabilityBandHigh: number | null;
  sillQualPrice: number | null;
  readinessScore: number | null;
  readinessSegment: string | null;
  readinessLabel: string | null;
  affordabilityMovement: AffordabilityMovement | null;
  downPaymentReady: boolean | null;
  // Passport preview — backend-derived bucketed ranges for the self-preview card.
  // Replaces client-side range derivation. Null when profile has no financial data.
  passportPreview: PassportPreview | null;
}

export type UseAgentPreviewState = {
  /** The projected agent-lens profile, null while loading / error / empty. */
  profile: AgentPreviewProfile | null;
  /** True only before the first response arrives (no cached data). */
  isLoading: boolean;
  /**
   * Set on any non-401 HTTP failure or network error. Distinct from
   * isUnauthenticated so the card can render targeted copy for each state.
   */
  error: Error | null;
  /**
   * True when the endpoint returns 401 (no Clerk session). The caller can
   * render a "sign in to see preview" state rather than a generic error.
   */
  isUnauthenticated: boolean;
};

type Phase = "idle" | "loading" | "loaded";

export type UseAgentPreviewOptions = {
  getToken: () => Promise<string | null>;
  /**
   * Monotonic counter. Increment to trigger a re-fetch of the agent-preview
   * endpoint. Mirrors the `refreshKey` pattern on useManualEntries — bump on
   * input/goal mutation SUCCESS so the re-fetch reads fresh snapshot data.
   * Defaults to 0 (fetch once on mount).
   */
  refreshKey?: number;
};

function passportPreviewFromWire(w: PassportPreviewWire): PassportPreview {
  return {
    overallTier: w.overall_tier ?? null,
    anonymizedHandle: w.anonymized_handle ?? null,
    estimatedQualificationLow: w.estimated_qualification_low ?? null,
    estimatedQualificationHigh: w.estimated_qualification_high ?? null,
    downPaymentLow: w.down_payment_low ?? null,
    downPaymentHigh: w.down_payment_high ?? null,
    downPaymentTier: w.down_payment_tier ?? null,
    householdIncomeLow: w.household_income_low ?? null,
    householdIncomeHigh: w.household_income_high ?? null,
    householdIncomeTier: w.household_income_tier ?? null,
    creditBand: w.credit_band ?? null,
    creditTier: w.credit_tier ?? null,
    employmentBand: w.employment_band ?? null,
    employmentTier: w.employment_tier ?? null,
    monthlyDebtLow: w.monthly_debt_low ?? null,
    monthlyDebtHigh: w.monthly_debt_high ?? null,
    monthlyDebtTier: w.monthly_debt_tier ?? null,
  };
}

function fromWire(w: AgentPreviewProfileWire): AgentPreviewProfile {
  return {
    buyerType: w.buyer_type ?? null,
    timeline: w.timeline ?? null,
    targetArea: w.target_area ?? null,
    targetPriceBand: w.target_price_band ?? null,
    priceKind: w.price_kind,
    priceApproximate: w.price_approximate,
    profileCompleteness: w.profile_completeness ?? null,
    displayName: w.display_name ?? null,
    housingSituation: w.housing_situation ?? null,
    creditScoreRange: w.credit_score_range ?? null,
    employmentMonths: w.employment_months ?? null,
    targetPrice: w.target_price ?? null,
    affordabilityBandLow: w.affordability_band_low ?? null,
    affordabilityBandHigh: w.affordability_band_high ?? null,
    sillQualPrice: w.sill_qual_price ?? null,
    readinessScore: w.readiness_score ?? null,
    readinessSegment: w.readiness_segment ?? null,
    readinessLabel: w.readiness_label ?? null,
    affordabilityMovement: w.affordability_movement
      ? {
          bandHighFrom: w.affordability_movement.band_high_from,
          bandHighTo: w.affordability_movement.band_high_to,
        }
      : null,
    downPaymentReady: w.down_payment_ready ?? null,
    passportPreview: w.passport_preview
      ? passportPreviewFromWire(w.passport_preview)
      : null,
  };
}

/**
 * useAgentPreview fetches GET /api/profile/agent-preview and returns the
 * agent-lens projection (qualitative + affordability outcome). Exposes three
 * distinct states: loading, error, and unauthenticated (401).
 *
 * StrictMode safety: the cleanup closure sets `cancelled = true` before the
 * async resolves, so a React StrictMode double-invoke (mount → cleanup →
 * remount) never leaves state stuck in "loading" and never writes stale data
 * from the cancelled effect.
 *
 * Never returns stale data from a prior render: state is scoped to the
 * component lifecycle; unmounting clears the in-flight async.
 */
export function useAgentPreview({
  getToken,
  refreshKey = 0,
}: UseAgentPreviewOptions): UseAgentPreviewState {
  const [profile, setProfile] = useState<AgentPreviewProfile | null>(null);
  const [phase, setPhase] = useState<Phase>("loading");
  const [error, setError] = useState<Error | null>(null);
  const [isUnauthenticated, setIsUnauthenticated] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // When refreshKey changes (e.g. after a mutation success), reset phase to
      // "loading" so the caller can show a refetch-in-progress state if desired.
      // Done as the first statement of the async body (runs synchronously up to
      // the first await) so there's no render between the reset and the fetch,
      // while keeping the setState out of the effect's synchronous body.
      setPhase("loading");
      try {
        // Race getToken() against a 4000 ms timeout so a hung / uninitialized
        // Clerk client (one that neither resolves nor rejects, e.g. in a
        // no-session preview) cannot block the fetch indefinitely.
        //
        // Production path: a healthy getToken() resolves in milliseconds,
        // always winning the race and supplying the real token — behavior is
        // unchanged.
        //
        // Hang path: after 4000 ms the timeout arm wins, resolving to null so
        // the fetch proceeds unauthenticated (endpoint returns 401, handled
        // below).
        //
        // The timer handle is captured so we can cancel it once the race
        // settles, preventing a dangling timer that would keep the event loop
        // alive after the component unmounts.
        let timeoutHandle: ReturnType<typeof setTimeout>;
        const timeoutPromise = new Promise<null>(
          (resolve) => (timeoutHandle = setTimeout(() => resolve(null), 4000)),
        );
        const token =
          (await Promise.race([
            getToken().catch(() => null),
            timeoutPromise,
          ]).finally(() => clearTimeout(timeoutHandle))) ?? undefined;
        const response = await apiRequest<AgentPreviewEnvelope>(
          "/api/profile/agent-preview",
          { token },
        );
        if (cancelled) return;
        setProfile(response.profile ? fromWire(response.profile) : null);
        setError(null);
        setIsUnauthenticated(false);
      } catch (e) {
        if (cancelled) return;
        // Distinguish 401 (unauthenticated) from generic errors so the card
        // can render targeted copy. Do NOT expose a generic error for 401.
        if (e instanceof ApiError && e.status === 401) {
          setIsUnauthenticated(true);
          setError(null);
          setProfile(null);
        } else {
          setError(e instanceof Error ? e : new Error(String(e)));
          setIsUnauthenticated(false);
          setProfile(null);
        }
      } finally {
        if (!cancelled) {
          setPhase("loaded");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getToken, refreshKey]);

  return {
    profile,
    isLoading: phase === "loading",
    error,
    isUnauthenticated,
  };
}
